import {
  WESTERN_PLANETS,
  type AspectOrbConfig,
  type ChartPointId,
  type GocharPlacement,
  type PlanetPosition,
  type TransitHit,
} from '@astrally/shared';
import type { EphemerisAdapter } from '../ephemeris/adapter.js';
import { computePositions } from '../core/positions.js';
import { signCount } from '../core/zodiac.js';
import { computeCrossAspects, type ChartPoint } from './aspects.js';

/** Tighter orbs are conventional for transits. */
const TRANSIT_ORBS: AspectOrbConfig = {
  conjunction: 3,
  opposition: 3,
  trine: 2.5,
  square: 2.5,
  sextile: 2,
  quincunx: 1.5,
  'semi-sextile': 1,
  'semi-square': 1,
  sesquiquadrate: 1,
  luminaryBonus: 1,
};

export interface TransitContext {
  at: Date;
  positions: PlanetPosition[];
}

export function currentPositions(at: Date, adapter: EphemerisAdapter): TransitContext {
  return { at, positions: computePositions(at, adapter) };
}

/** Western transits: aspects from moving planets to natal points. */
export function computeTransits(
  natalPoints: ChartPoint[],
  ctx: TransitContext,
): TransitHit[] {
  const moving: ChartPoint[] = ctx.positions
    .filter((p) => WESTERN_PLANETS.includes(p.planet))
    .map((p) => ({ id: p.planet as ChartPointId, lon: p.lonTropical }));
  return computeCrossAspects(moving, natalPoints, TRANSIT_ORBS).map((a) => ({
    transiting: a.a as TransitHit['transiting'],
    natal: a.b,
    type: a.type,
    orb: a.orb,
  }));
}

/** Jyotish gochar: current sidereal placements, houses counted from natal Moon. */
export function computeGochar(
  ctx: TransitContext,
  natalMoonSign?: number,
): GocharPlacement[] {
  return ctx.positions
    .filter((p) => !['uranus', 'neptune', 'pluto'].includes(p.planet))
    .map((p) => ({
      planet: p.planet,
      sidereal: p.sidereal,
      nakshatra: p.nakshatra,
      retrograde: p.retrograde,
      houseFromMoon:
        natalMoonSign !== undefined ? signCount(natalMoonSign, p.sidereal.sign) : undefined,
    }));
}
